// @flow
import React, {useEffect, useState} from 'react';
import {useHistory, useParams} from "react-router-dom";
import Header from "../Header";
import BackButton from "../../GUI_COMMON/COMPONENTS/BackButton";
import TextInput from "../../GUI_COMMON/COMPONENTS/TextInput";
import LIButton from "../../GUI_COMMON/COMPONENTS/LIButton";
import Popup from "../../GUI_COMMON/COMPONENTS/Popup";
import ArchiverClientApi from "../../API/archiverClientApi";
import ArchiverGuiUtils from "../../SUPPORT/ArchiverGuiUtils";
import LocationSelector from "../LocationSelector";
import LIUtils from "../../GUI_COMMON/SUPPORT/LIUtils";
import TextArea from "../../GUI_COMMON/COMPONENTS/TextArea";
import type {BTOBox} from "../BTOAddUpdatePage";

const BoxMoveAdminPage = () => {

    const [, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [curLocationDetails, setCurLocationDetails] = useState(getDefaultLocation());
    const [destLocationDetails, setDestLocationDetails] = useState(getDefaultLocation());
    const [notes, setNotes] = useState("");
    const [curBoxDetails:BTOBox, setCurBoxDetails] = useState(null);
    const [fullLocationsList, setFullLocationsList] = useState([]);
    const [isDirty, setIsDirty] = useState(false);
    
    const {boxBC} = useParams();
    const [boxBarcode, setBoxBarcode] = useState(boxBC?boxBC:"");
    
    const history = useHistory();

    useEffect(() => {
        console.debug("refreshing locations list");
        getLocationsList();
    }, []);
    
    useEffect( () => {
        if(!boxBarcodeLooksGood(boxBarcode)) { // ignore
            return;
        }
        
        console.debug("getting box details for "+boxBarcode);
        ArchiverClientApi.sendBoxSearchRequest(boxBarcode,0,1)
        .then((results) => { // returns at most 1 result
            const boxDetails:BTOBox = results[0];
            setCurBoxDetails(boxDetails);
            
            const locStr = ArchiverGuiUtils.GetLocationBasedOnDeptId(boxDetails?.storageLocationId, fullLocationsList);
            const [locationId,deptId] = LIUtils.getLocationAndDeptFromSingleLocationString(locStr, fullLocationsList);
            setCurLocationDetails({
                locationId, deptId
            });
            setDestLocationDetails({
                locationId, deptId
            });
        })
        .catch( responseError => {
            ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError);
            setCurBoxDetails(null);
        });
    },[boxBarcode, fullLocationsList]);

    function getLocationsList() {
        setIsLoading(true);
        ArchiverClientApi.getLocations(true) // request all enabled locations
        .then( (locations) => {
            setFullLocationsList(locations??[]);
        })
        .catch( responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally( () => {
            setIsLoading(false);
        });
    }
    
    function getDefaultLocation() {
        return {
            locationId:-1,
            deptId:-1
        }
    }

    function onChange(e) {
        const thing = e.target.name;
        let value = e.target.value;
        
        const locationEditInfo = {...destLocationDetails};
        if(thing==="boxBarcode") {
            setBoxBarcode(value);
        }
        else if(thing==="toLocation-destLocation") { // value here is the id selected
            const locId = Number(value);
            if(isNaN(locId)) {
                locationEditInfo.locationId=-1;
                locationEditInfo.deptId=-1;   // reset
            }
            else {
                locationEditInfo.locationId=locId;
                locationEditInfo.deptId=-1;
            }
        }
        else if(thing==="toLocation-destDepartment") { // value here is the id selected
            locationEditInfo.deptId=Number(value);
        }
        else if(thing==="notes") {
            setNotes(value);
        }

        setDestLocationDetails(locationEditInfo);
        setIsDirty(true);
    }

    function checkForValid() {
        if(!boxBarcodeLooksGood(boxBarcode)) {
            return false;
        }
        if(curBoxDetails===null) {
            return false;
        }
        if(destLocationDetails.locationId===-1 || destLocationDetails.deptId===-1) {
            return false;
        }
        if(notes!=="") { // we allow valid if the dest location is the same, but we have new notes
            return true;
        }
        if(destLocationDetails.locationId===curLocationDetails.locationId && destLocationDetails.deptId===curLocationDetails.deptId) {
            return false;
        }
        return true;
    }

    function boxBarcodeLooksGood(boxBC) {
        if(!boxBC || boxBC==="") {
            return false;
        }
        const re = new RegExp('^[0-9][0-9][a-zA-Z]*.[0-9]{4}$');
        if(!re.test(boxBC)) { // not complete
            console.log("bad box barcode "+boxBC);
            return false;
        }
        return true; // good
    }

    function handleCancelClicked(e) {
        e.preventDefault();
        e.stopPropagation();

        confirmClearDirty();
    }

    function handleSaveClicked(e) {
        e.preventDefault();
        e.stopPropagation();

        if(!checkForValid()) {
            return;
        }

        setIsSaving(true);
        const childMagsToo = true;
        ArchiverClientApi.sendMoveBoxRequest(curBoxDetails.id,destLocationDetails.deptId, notes, childMagsToo)
        .then( (savedLocationInfo) => {
            Popup.showInfo(`Box '${boxBarcode}' was successfully moved to '${savedLocationInfo}'`);
            setIsDirty(false);
            // clear out the stored boxinfo?
        })
        .catch( responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally( () => {
            setIsSaving(false);
        });
    }

    function confirmClearDirty() {
        if(isDirty) {
            Popup.showConfirm("Cancel?","You have changed some information.","Forget changes",null,
                () => {
                    resetLocationDetailsShown();
                });
        }
        else {
            resetLocationDetailsShown(); // just clear what's there
        }
    }

    function updateUrlLink(toThis) {
        history.replace(toThis);
    }

    function resetLocationDetailsShown() {
        setCurLocationDetails(getDefaultLocation());
        setDestLocationDetails(getDefaultLocation());
        setBoxBarcode("");
        setNotes("");
        setIsDirty(false);
        updateUrlLink("/admin/box_move"); // blank

        // focus the first input field
        const nameEl = document.getElementsByName("name")[0];
        if(nameEl) {
            nameEl.focus();
        }
    }
    
    function handleBackClicked() {
        history.goBack();
    }
    
    return (
        <div className="container">
            <Header hideAdmin/>
            <div className="li-header-row">
                <BackButton onClick={handleBackClicked}/>
                <h1 className="ml-4 d-inline-block align-middle">Box Move</h1>
            </div>
            <p>Use this page to move a single box from one location to another.  The box must already exist in the system.</p>
            <p className="li-border-round li-bg-warn-light px-2 p-1">* This is ALMOST ALWAYS NOT what you want to do.  You should always move boxes to or 
                from locations using Box Transfer Orders.</p>
            <p className="li-border-round li-bg-warn px-2 p-1">* Moving boxes using this page will also <b>automatically update the locations of
                any of the magazines and samples in that box</b>.<br/>
                This includes marking things off-premises if the location is off-premises, locking any samples in the box.
            <br/><br/>** USE THIS PAGE WITH CAUTION **</p>

            <hr/>
                <div className="row px-3 pb-3 li-bg-light">
                    <div className="d-flex align-items-center">
                        <TextInput onChange={onChange} label="Box Barcode" name="boxBarcode" widthPels={600} oneRow placeholder="##<neumonic>.####"
                                   value={boxBarcode} showClearButton goodNess={(curBoxDetails!==null && boxBarcodeLooksGood(boxBarcode))?'good':'bad'}
                                   helpText="* this box needs to already exist in the system"/>
                        
                    </div>
                </div>
                {/*<hr/>*/}
                <div className="row">
                    <div className="col-6 mt-3">
                        <h3 className="text-center">FROM</h3>
                        <div className="li-border-round li-border-dark p-2">
                            <div className="row px-3 pb-3 li-bg-light">
                                <div className="col-12">
                                    <LocationSelector name="fromLocation" onChange={onChange} locationLabel="Location"
                                                      locationValue={curLocationDetails.locationId}
                                                      departmentValue={curLocationDetails.deptId}
                                                      onIsValid={checkForValid}
                                                      showContact={false}
                                                      showOffPremises={true}
                                                      readOnly
                                                      withDepartmentsOnly/>
                                </div>
                            </div>
                        </div> 
                    </div>
                    <div className="col-6 mt-3">
                        <h3 className="text-center">TO</h3>
                        <div className="li-border-round li-border-dark p-2">
                            <div className="row px-3 pb-3 li-bg-light">
                                <div className="col-12">
                                    <LocationSelector name="toLocation" onChange={onChange} locationLabel="Location"
                                                      locationValue={destLocationDetails.locationId}
                                                      departmentValue={destLocationDetails.deptId}
                                                      onIsValid={checkForValid}
                                                      showContact={false}
                                                      showOffPremises={true}
                                                      required={true}
                                                      withDepartmentsOnly/>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="col-12 mt-3">
                        <TextArea onChange={onChange} label="Notes" name="notes" placeholder="Notes" value={notes} rows={3}/>
                    </div>
                </div>
            
            <div className="text-center my-5">
                <LIButton name="cancel" label={isDirty?"Cancel":"Clear"} bgClass="mr-4" primary onClick={handleCancelClicked}/>
                <LIButton bgClass="" primary onClick={handleSaveClicked} label="Move" showBusy={isSaving} disabled={!(isDirty && checkForValid())}/>
            </div>
        </div>
    );
}

BoxMoveAdminPage.propTypes = {
};

export default BoxMoveAdminPage;